import { computed, type ComputedRef } from 'vue'
import type { Product } from '@/types/product'
import { hasKnownAvailableQuantity, splitQuantityByAvailability, type AvailabilitySplit } from '@/utils/deliveryAvailability'

interface DeliveryStatus {
  isInStock: ComputedRef<boolean>
  isDiscontinued: ComputedRef<boolean>
  deliveryWeeks: ComputedRef<string>
  statusColor: ComputedRef<string>
  isPreOrder: ComputedRef<boolean>
  isMonths: ComputedRef<boolean>
  /** True when a non-POS product has no immediate stock and no usable replenishment ("Ausverkauft"). */
  isSoldOut: ComputedRef<boolean>
  /** Immediately-available units for this SKU, or null when unknown. */
  availableQuantity: ComputedRef<number | null>
  /** True when the API provided a usable available quantity to display. */
  hasAvailableQuantity: ComputedRef<boolean>
  /** Split a chosen line quantity into the part that ships now and the part that becomes a pre-order. */
  splitFor: (quantity: number) => AvailabilitySplit
}

/**
 * Composable for product delivery status logic
 * Provides consistent delivery status across all components
 */
export function useDeliveryStatus(product: ComputedRef<Product> | Product): DeliveryStatus {
  const getProduct = () => {
    return 'value' in product ? product.value : product
  }

  const isDiscontinued = computed(() => {
    const prod = getProduct()
    return prod.discontinued === true
  })

  const isPreOrder = computed(() => {
    const prod = getProduct()
    return !isDiscontinued.value && prod.preOrder === true
  })

  // Sold out = non-POS product with 0 immediate stock AND no usable replenishment. POS is never
  // sold out (governed by the max-1 rule), and an unknown availability (null) is not sold out.
  const isSoldOut = computed(() => {
    const prod = getProduct()
    return !isDiscontinued.value && !prod.isPos && prod.soldOut === true
  })

  const isInStock = computed(() => {
    const prod = getProduct()
    const weeksFrom = prod.deliveryInWeeksFrom || 0
    const weeksTo = prod.deliveryInWeeksTo || 0
    // Product is in stock if delivery is within 0 weeks (immediately available)
    return !prod.hide && !isDiscontinued.value && weeksFrom === 0 && weeksTo === 0 && !isPreOrder.value
  })

  const deliveryWeeks = computed(() => {
    const prod = getProduct()
    const weeksFrom = prod.deliveryInWeeksFrom || 0
    const weeksTo = prod.deliveryInWeeksTo || 0

    // Convert to months if 8+ weeks (using "ca" prefix for approximately)
    if (weeksFrom >= 8 || weeksTo >= 8) {
      const monthsFrom = Math.floor(weeksFrom / 4)
      const monthsTo = Math.floor(weeksTo / 4)
      return monthsFrom === monthsTo ? `ca ${monthsFrom}` : `ca ${monthsFrom}-${monthsTo}`
    }

    return weeksFrom === weeksTo ? `${weeksFrom}` : `${weeksFrom}-${weeksTo}`
  })

  const isMonths = computed(() => {
    const prod = getProduct()
    const weeksFrom = prod.deliveryInWeeksFrom || 0
    const weeksTo = prod.deliveryInWeeksTo || 0
    return weeksFrom >= 8 || weeksTo >= 8
  })

  const statusColor = computed(() => {
    if (isDiscontinued.value) {
      return 'bg-gray-400'
    }

    const prod = getProduct()
    const weeksFrom = prod.deliveryInWeeksFrom || 0
    const weeksTo = prod.deliveryInWeeksTo || 0

    if (weeksFrom === 0 && weeksTo === 0) {
      return 'bg-green-500'  // In Stock
    } else if (Math.max(weeksFrom, weeksTo) <= 3) {
      return 'bg-yellow-500' // 1-3 weeks
    } else {
      return 'bg-red-500'    // Over 3 weeks
    }
  })

  const availableQuantity = computed<number | null>(() => {
    const prod = getProduct()
    return hasKnownAvailableQuantity(prod.availableQuantity) ? (prod.availableQuantity as number) : null
  })

  const hasAvailableQuantity = computed(() => availableQuantity.value !== null)

  const splitFor = (quantity: number): AvailabilitySplit => {
    return splitQuantityByAvailability(quantity, getProduct().availableQuantity)
  }

  return {
    isInStock,
    isDiscontinued,
    deliveryWeeks,
    statusColor,
    isPreOrder,
    isMonths,
    isSoldOut,
    availableQuantity,
    hasAvailableQuantity,
    splitFor,
  }
}
